import { FormEvent, useEffect, useState } from "react";
import { kpsiGet, kpsiPost } from "./kpsiApi";
import KpsiInterpreterGrid from "./KpsiInterpreterGrid";

type Detail = {
  test: {
    id: number;
    firstName: string;
    lastName: string;
    position: string;
    dateFaxDisplay: string;
    stage: number;
    stageName: string;
    score: string;
    education?: string;
    adj?: string;
    range?: string;
    sort?: string;
    eng?: string;
    green?: string;
    red?: string;
    blue?: string;
    yellow?: string;
    notes?: string;
    downloadDisplay?: string;
    color: string;
    combinedDes: string;
    combinedWord: string;
  };
  work: Array<{ position?: string; startDate?: string; endDate?: string; reason?: string }>;
  companyName: string;
  isInterpreter: boolean;
  isScorer: boolean;
  canEditNotes: boolean;
};

interface Props {
  id: number;
  onBack: () => void;
  onEdit: (id: number) => void;
  onPrint: (id: number) => void;
  onFlash: (msg: string) => void;
}

export default function KpsiTestView({ id, onBack, onEdit, onPrint, onFlash }: Props) {
  const [data, setData] = useState<Detail | null>(null);
  const [score, setScore] = useState("");
  const [notes, setNotes] = useState("");

  async function load() {
    const result = await kpsiGet<Detail>(`/Kpsi/Tests/${id}`);
    setData(result);
    setScore(result.test.score ?? "");
    setNotes(result.test.notes ?? "");
  }

  useEffect(() => {
    void load().catch((e) => onFlash(e instanceof Error ? e.message : "Load failed"));
  }, [id]);

  if (!data) return <p className="kpsi-muted">Loading…</p>;
  const t = data.test;
  const fullName = `${t.firstName} ${t.lastName}`.trim();

  async function onScore(e: FormEvent) {
    e.preventDefault();
    await kpsiPost(`/Kpsi/Tests/${id}/Score`, { score });
    onFlash("Score saved.");
    await load();
  }

  async function onDownloaded() {
    await kpsiPost(`/Kpsi/Tests/${id}/Downloaded`);
    onFlash("Marked as downloaded.");
    await load();
  }

  async function onCompleted() {
    await kpsiPost(`/Kpsi/Tests/${id}/Completed`);
    onFlash("Marked as completed.");
    await load();
  }

  async function onNotes(e: FormEvent) {
    e.preventDefault();
    await kpsiPost(`/Kpsi/Tests/${id}/Notes`, { notes });
    onFlash("Notes saved.");
    await load();
  }

  return (
    <>
      <p className="kpsi-no-print">
        <button type="button" onClick={onBack}>&laquo; Back</button>
        {" | "}
        <button type="button" onClick={() => onPrint(id)}>Print View</button>
        {" | "}
        <button type="button" onClick={() => onEdit(id)}>Edit this record</button>
      </p>

      {t.stage === 1 && data.isInterpreter && (
        <div className="kpsi-actions-required kpsi-no-print">
          <form onSubmit={(e) => void onScore(e)}>
            <span>When ready, enter a score</span>{" "}
            <input value={score} onChange={(e) => setScore(e.target.value)} required />{" "}
            <button type="submit">Save</button>
          </form>
        </div>
      )}
      {t.stage === 2 && data.isInterpreter && (
        <div className="kpsi-actions-required kpsi-no-print">
          <span>Have you downloaded the KPSI and alerted the client?</span>{" "}
          <button type="button" onClick={() => void onDownloaded()}>Yes</button>
        </div>
      )}
      {t.stage === 3 && data.isScorer && (
        <div className="kpsi-actions-required kpsi-no-print">
          <span>Have you completed this KPSI?</span>{" "}
          <button type="button" onClick={() => void onCompleted()}>Yes</button>
        </div>
      )}

      <h1>
        Viewing Test for <em>{fullName}</em>
        <br />
        <small>
          Faxed on <em>{t.dateFaxDisplay}</em>
          {data.companyName ? <> &middot; {data.companyName}</> : null}
        </small>
      </h1>

      <p>
        <i>Current Stage:</i> <b>{t.stageName}</b>
        <br />
        <i>Download Date:</i> <b>{t.downloadDisplay || "—"}</b>
      </p>

      <KpsiInterpreterGrid test={t} />

      <div className="kpsi-card">
        <div className="kpsi-card-header">Education / Skills</div>
        <div className="kpsi-card-body">
          <pre>{t.education}</pre>
        </div>
      </div>

      <div className="kpsi-card">
        <div className="kpsi-card-header">Previous Work Experience</div>
        <div className="kpsi-card-body">
          {!data.work?.length ? (
            <p className="kpsi-muted">None entered.</p>
          ) : (
            <ol>
              {data.work.map((w, i) => (
                <li key={i} className="kpsi-work-row">
                  <strong>{w.position}</strong>
                  <br />
                  {w.startDate} – {w.endDate}
                  <br />
                  <span className="kpsi-muted">Reason: {w.reason}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <div className="kpsi-card">
        <div className="kpsi-card-header">Notes</div>
        <div className="kpsi-card-body">
          {data.canEditNotes ? (
            <form onSubmit={(e) => void onNotes(e)} className="kpsi-no-print">
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={8} />
              <div><button type="submit">Save Notes</button></div>
            </form>
          ) : (
            <pre>{t.notes}</pre>
          )}
        </div>
      </div>
    </>
  );
}
